import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";

/**
 * The package's own menu (0.17.0): the settings page is meant to be pasted into any dashboard, and
 * a native `<select>` opens a menu the dashboard cannot dress — the one control out of place.
 *
 * Accessible like the native one: `combobox` + `listbox`/`option`, `aria-expanded`,
 * `aria-activedescendant`; arrows, Home/End, Enter/Space to pick, Escape to close, typing jumps to
 * the option that starts with the letter; a click outside closes. The menu opens upwards when there
 * is no room below, is at least as wide as the field, and its options are at least 40 px tall.
 */
export function Dropdown({
  label,
  value,
  options,
  disabled,
  onChange,
  style,
  className,
  menuStyle,
}: {
  label: string;
  value: string;
  options: ReadonlyArray<{ value: number; label: string }>;
  disabled: boolean;
  onChange: (value: number) => void;
  style: CSSProperties;
  className?: string;
  menuStyle?: CSSProperties;
}) {
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [up, setUp] = useState(false);
  const selected = Math.max(0, options.findIndex((o) => String(o.value) === value));
  const [active, setActive] = useState(selected);

  useEffect(() => {
    if (!open) return;
    const outside = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, [open]);

  const show = () => {
    const box = root.current?.getBoundingClientRect();
    setUp(!!box && window.innerHeight - box.bottom < Math.min(280, options.length * 42 + 12));
    setActive(selected);
    setOpen(true);
  };
  const pick = (index: number) => {
    setOpen(false);
    const option = options[index];
    if (option && String(option.value) !== value) onChange(option.value);
  };
  const keys = (event: KeyboardEvent) => {
    if (disabled) return;
    const last = options.length - 1;
    const move = (index: number) => {
      event.preventDefault();
      if (!open) show();
      setActive(Math.min(last, Math.max(0, index)));
    };
    if (event.key === "ArrowDown") return move(open ? active + 1 : selected);
    if (event.key === "ArrowUp") return move(open ? active - 1 : selected);
    if (event.key === "Home") return move(0);
    if (event.key === "End") return move(last);
    if (event.key === "Escape" && open) {
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      return open ? pick(active) : show();
    }
    if (event.key.length === 1 && /\S/.test(event.key)) {
      const letter = event.key.toLowerCase();
      const from = open ? active + 1 : selected + 1;
      const order = [...options.slice(from), ...options.slice(0, from)];
      const hit = order.find((o) => o.label.toLowerCase().startsWith(letter));
      if (hit) move(options.indexOf(hit));
    }
  };

  return (
    <div ref={root} style={{ position: "relative", flexShrink: 0 }}>
      <button
        type="button"
        role="combobox"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={`${id}-list`}
        aria-activedescendant={open ? `${id}-${active}` : undefined}
        disabled={disabled}
        className={className}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={keys}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          textAlign: "left",
          cursor: disabled ? "default" : "pointer",
          ...style,
        }}
      >
        <span>{options[selected]?.label ?? value}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"
          style={{ transform: open ? "rotate(180deg)" : undefined, transition: "transform .15s" }}>
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <ul
          id={`${id}-list`}
          role="listbox"
          aria-label={label}
          style={{
            position: "absolute",
            zIndex: 50,
            left: 0,
            minWidth: "100%",
            maxHeight: 280,
            overflowY: "auto",
            margin: 0,
            padding: 4,
            listStyle: "none",
            borderRadius: 10,
            border: "1px solid rgba(15,23,42,.15)",
            background: "#fff",
            color: "#0f172a",
            boxShadow: "0 16px 40px -16px rgba(2,6,23,.35)",
            ...(up ? { bottom: "calc(100% + 4px)" } : { top: "calc(100% + 4px)" }),
            ...menuStyle,
          }}
        >
          {options.map((option, index) => (
            <li
              key={option.value}
              id={`${id}-${index}`}
              role="option"
              aria-selected={index === selected}
              onMouseEnter={() => setActive(index)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => pick(index)}
              style={{
                display: "flex",
                alignItems: "center",
                minHeight: 40,
                padding: "0 12px",
                borderRadius: 8,
                cursor: "pointer",
                whiteSpace: "nowrap",
                fontWeight: index === selected ? 600 : 400,
                background: index === active ? "rgba(37,99,235,.10)" : "transparent",
              }}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
